'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import type { CalendarDay, DateInterval } from '@/types/calendar';
import {
  compareDates,
  isDateBetween,
  getDatesBetween,
  addOneDay,
  addDays,
} from '@/lib/calendar-utils';
import { computeBestRate } from '@/lib/calendar/helpers';
import { MinStayNotice } from './min-stay-notice';

interface MiniCalendarProps {
  roomSlug: string;
  /** Controlled interval state — parent owns it */
  interval: DateInterval;
  onIntervalChange: (interval: DateInterval) => void;
  onPriceLoaded?: (minPrice: number | null, currency: string) => void;
  /** Called with all fetched days so parent can compute totals */
  onCalendarDataChange?: (days: CalendarDay[]) => void;
  /** Pre-loaded days from SSR or parent bulk fetch (controlled mode) */
  initialDays?: CalendarDay[];
  /** Currency for the controlled data */
  initialCurrency?: string;
  /**
   * Server-provided "starting from" price for the displayed month.
   * Already includes the next-month fallback when the queried month
   * has no usable price; pass it through so the parent card never
   * blanks out its price label.
   */
  initialMinPrice?: number | null;
  /** Controlled year — when provided, MiniCalendar doesn't manage its own month state */
  controlledYear?: number;
  /** Controlled month — when provided, MiniCalendar doesn't manage its own month state */
  controlledMonth?: number;
  /** Called when user navigates months in controlled mode */
  onMonthChange?: (year: number, month: number) => void;
  /** Whether parent is currently loading data (controlled mode) */
  isParentLoading?: boolean;
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

/**
 * Client-side fallback for the "starting from" price when the server
 * didn't emit `minPrice`. Drops the `available` filter on purpose: PYN's
 * rate-card price is independent of per-day availability, so a fully
 * booked month still has prices we can advertise.
 */
function deriveMinPrice(days: CalendarDay[]): number | null {
  const prices = days
    .filter((d) => !d.isPast && d.price !== null)
    .map((d) => d.price as number);
  return prices.length > 0 ? Math.min(...prices) : null;
}

export function MiniCalendar({
  roomSlug,
  interval,
  onIntervalChange,
  onPriceLoaded,
  onCalendarDataChange,
  initialDays,
  initialCurrency,
  initialMinPrice,
  controlledYear,
  controlledMonth,
  onMonthChange,
  isParentLoading,
}: MiniCalendarProps) {
  const t = useTranslations('common');
  const now = new Date();

  // Controlled mode: parent manages year/month and provides data
  const isControlled = controlledYear !== undefined && controlledMonth !== undefined;

  // Internal state for uncontrolled mode
  const [internalYear, setInternalYear] = useState(now.getFullYear());
  const [internalMonth, setInternalMonth] = useState(now.getMonth() + 1);

  // Effective year/month
  const year = isControlled ? controlledYear! : internalYear;
  const month = isControlled ? controlledMonth! : internalMonth;

  // Days state (used in uncontrolled mode; in controlled mode, initialDays is used directly)
  const [days, setDays] = useState<CalendarDay[]>(initialDays || []);
  const [internalLoading, setInternalLoading] = useState(!initialDays);
  const isLoading = isControlled ? (isParentLoading ?? false) : internalLoading;

  // In controlled mode, use initialDays directly for rendering — no effect→state→render gap
  const renderDays = useMemo(
    () => isControlled ? (initialDays || []) : days,
    [isControlled, initialDays, days]
  );

  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const cache = useRef<Map<string, CalendarDay[]>>(new Map());
  // Accumulate all fetched days across months for range validation & price calc
  const allDays = useRef<Map<string, CalendarDay>>(new Map());

  // Track which month key we last processed to avoid re-processing same data
  const lastProcessedKey = useRef<string>('');

  // Process initial data on mount
  useEffect(() => {
    if (!initialDays || initialDays.length === 0) return;

    const dataMonth = initialDays[0].date.substring(0, 7);
    const dataKey = `${roomSlug}-${dataMonth}`;
    const cacheKey = `${roomSlug}-${year}-${month}`;
    lastProcessedKey.current = dataKey;
    cache.current.set(cacheKey, initialDays);

    for (const d of initialDays) {
      allDays.current.set(d.date, d);
    }
    onCalendarDataChange?.(Array.from(allDays.current.values()));

    onPriceLoaded?.(
      initialMinPrice ?? deriveMinPrice(initialDays),
      initialCurrency || 'EUR'
    );
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Controlled mode: process side-effects when parent provides new data
  // (rendering uses initialDays directly via renderDays — no state needed)
  useEffect(() => {
    if (!isControlled || !initialDays || initialDays.length === 0) return;

    const dataMonth = initialDays[0].date.substring(0, 7);
    const dataKey = `${roomSlug}-${dataMonth}`;

    if (dataKey === lastProcessedKey.current) return;
    lastProcessedKey.current = dataKey;

    // Update cross-month data for range validation
    for (const d of initialDays) {
      allDays.current.set(d.date, d);
    }
    onCalendarDataChange?.(Array.from(allDays.current.values()));

    onPriceLoaded?.(
      initialMinPrice ?? deriveMinPrice(initialDays),
      initialCurrency || 'EUR'
    );
  }, [isControlled, initialDays, initialCurrency, initialMinPrice, roomSlug, onCalendarDataChange, onPriceLoaded]);

  // Uncontrolled mode: fetch calendar data
  const fetchCalendar = useCallback(async (slug: string, y: number, m: number) => {
    const key = `${slug}-${y}-${m}`;
    const cached = cache.current.get(key);
    if (cached) {
      setDays(cached);
      setInternalLoading(false);
      return;
    }

    setInternalLoading(true);
    try {
      const res = await fetch(`/api/calendar/${slug}?year=${y}&month=${m}`);
      const data = await res.json();
      if (data.success && data.data?.days) {
        cache.current.set(key, data.data.days);
        setDays(data.data.days);

        for (const d of data.data.days) {
          allDays.current.set(d.date, d);
        }
        onCalendarDataChange?.(Array.from(allDays.current.values()));

        const minPrice = data.data.minPrice ?? deriveMinPrice(data.data.days);
        onPriceLoaded?.(minPrice, data.data.currency || 'EUR');
      } else {
        setDays([]);
      }
    } catch {
      setDays([]);
    } finally {
      setInternalLoading(false);
    }
  }, [onPriceLoaded, onCalendarDataChange]);

  // Uncontrolled mode: trigger fetch on month change
  useEffect(() => {
    if (isControlled) return;
    fetchCalendar(roomSlug, internalYear, internalMonth);
  }, [isControlled, roomSlug, internalYear, internalMonth, fetchCalendar]);

  // Reset to current month when room changes (uncontrolled only)
  useEffect(() => {
    if (isControlled) return;
    const n = new Date();
    setInternalYear(n.getFullYear());
    setInternalMonth(n.getMonth() + 1);
    cache.current.clear();
    allDays.current.clear();
  }, [isControlled, roomSlug]);

  // Navigation
  const canGoPrev = () => {
    const today = new Date();
    return !(year < today.getFullYear() || (year === today.getFullYear() && month <= today.getMonth() + 1));
  };

  const goPrev = () => {
    if (!canGoPrev()) return;
    const newMonth = month === 1 ? 12 : month - 1;
    const newYear = month === 1 ? year - 1 : year;

    if (isControlled && onMonthChange) {
      onMonthChange(newYear, newMonth);
    } else {
      if (month === 1) { setInternalYear(y => y - 1); setInternalMonth(12); }
      else { setInternalMonth(m => m - 1); }
    }
  };

  const goNext = () => {
    const newMonth = month === 12 ? 1 : month + 1;
    const newYear = month === 12 ? year + 1 : year;

    if (isControlled && onMonthChange) {
      onMonthChange(newYear, newMonth);
    } else {
      if (month === 12) { setInternalYear(y => y + 1); setInternalMonth(1); }
      else { setInternalMonth(m => m + 1); }
    }
  };

  // Flash state for blocked dates (visual feedback when range is invalid)
  const [flashDates, setFlashDates] = useState<Set<string>>(new Set());
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Find unavailable dates in a range. A night with no loaded data (a month
  // not yet downloaded) is treated as blocked — "cannot verify" fails safe
  // rather than assuming available (docs/82 · A7), mirroring AvailabilityCalendar.
  const getBlockedDatesInRange = useCallback((checkIn: string, checkOut: string): string[] => {
    const datesInRange = getDatesBetween(checkIn, checkOut);
    return datesInRange.filter((date) => {
      const dayData = allDays.current.get(date);
      return !dayData || dayData.available === false;
    });
  }, []);

  // Selection phase tracks the 3-click cycle on top of the 1-night-default:
  //   'idle'        — no selection
  //   'one-night'   — a fresh 1-night default is in place; next click *may* extend
  //   'multi-night' — user has committed (extended or URL-seeded); next click restarts
  type SelectionPhase = 'idle' | 'one-night' | 'multi-night';
  const [phase, setPhase] = useState<SelectionPhase>(() => {
    if (interval.checkIn && interval.checkOut) return 'multi-night';
    if (interval.checkIn) return 'one-night';
    return 'idle';
  });

  // External clears (e.g. parent's "clear dates" button) snap phase back to idle.
  useEffect(() => {
    if (!interval.checkIn && !interval.checkOut) setPhase('idle');
  }, [interval.checkIn, interval.checkOut]);

  // Hover preview only renders while we're still in the "one extend chance" window.
  const selectionMode = phase === 'one-night' ? 'check-out' : 'check-in';

  // Handle day click — booking.com-style 3-click cycle adapted for the 1-night default:
  //   Click 1 → set check-in + check-out = check-in+1 (phase → one-night)
  //   Click 2 (later) → extend check-out (phase → multi-night)
  //   Click 2 (at/before) → restart with 1-night on clicked (stays one-night)
  //   Click 3 (any) → restart with 1-night on clicked (phase → one-night)
  const handleDayClick = useCallback((dayData: CalendarDay) => {
    if (dayData.isPast) return;

    const flash = (dates: string[]) => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      setFlashDates(new Set(dates));
      flashTimeoutRef.current = setTimeout(() => setFlashDates(new Set()), 1500);
    };

    const { checkIn } = interval;

    // Min-stay-aware first selection (doc 81 §12.3): an available check-in
    // auto-fills check-out to checkIn + its min-stay (1 normally, e.g. 3 on a
    // festive day). Falls back to a 1-night default if the auto-range crosses a
    // blocked night (dead-end arrival, §12.5).
    const startSelection = (d: CalendarDay) => {
      const autoCheckOut = addDays(d.date, d.minStay);
      const co =
        d.minStay > 1 && getBlockedDatesInRange(d.date, autoCheckOut).length > 0
          ? addOneDay(d.date)
          : autoCheckOut;
      onIntervalChange({ checkIn: d.date, checkOut: co });
      setPhase('one-night');
    };

    // Extend branch — only in the one-night phase, only for a strictly-later click.
    if (phase === 'one-night' && checkIn && compareDates(dayData.date, checkIn) > 0) {
      const minStay = allDays.current.get(checkIn)?.minStay ?? 1;
      const minCheckOut = addDays(checkIn, minStay);
      // Snap a below-minimum pick up to the min-stay floor.
      const targetCheckOut = compareDates(dayData.date, minCheckOut) < 0 ? minCheckOut : dayData.date;
      const blocked = getBlockedDatesInRange(checkIn, targetCheckOut);
      if (blocked.length > 0) {
        if (dayData.available) {
          startSelection(dayData);
        } else {
          flash([...blocked, dayData.date]);
          onIntervalChange({ checkIn: null, checkOut: null });
          setPhase('idle');
        }
        return;
      }
      onIntervalChange({ checkIn, checkOut: targetCheckOut });
      setPhase('multi-night');
      return;
    }

    // Restart branch — idle, multi-night (any click), or one-night with click at/before check-in.
    if (!dayData.available) {
      flash([dayData.date]);
      // Match old "click 3" UX: clear an already-committed multi-night on unavailable click.
      // For one-night phase, preserve the current 1-night default (matches old click-2 UX).
      if (phase === 'multi-night') {
        onIntervalChange({ checkIn: null, checkOut: null });
        setPhase('idle');
      }
      return;
    }
    startSelection(dayData);
  }, [interval, phase, getBlockedDatesInRange, onIntervalChange]);

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month - 1, 1);
  const startingDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month, 0).getDate();

  // Find min price for "best rate" highlight (festive days excluded — see
  // computeBestRate: a pricey holiday must not green the rest of the month).
  const { bestRatePrice, hasPriceVariation } = useMemo(
    () => computeBestRate(renderDays),
    [renderDays]
  );

  const getDayData = (dayNum: number): CalendarDay | undefined => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    return renderDays.find(d => d.date === dateStr);
  };

  // Min-stay floor for the active check-in during check-out selection: the
  // earliest valid check-out (checkIn + its min-stay). null when no festive
  // minimum applies. (doc 81 §12.3)
  const checkInMinStay = interval.checkIn ? (allDays.current.get(interval.checkIn)?.minStay ?? 1) : 1;
  const minStayFloorDate =
    phase === 'one-night' && interval.checkIn && checkInMinStay > 1
      ? addDays(interval.checkIn, checkInMinStay)
      : null;
  // Min-stay nights for the notice banner (selected or hovered check-in).
  const bannerAnchor = interval.checkIn ?? hoveredDate;
  const activeMinStay = bannerAnchor ? (allDays.current.get(bannerAnchor)?.minStay ?? 1) : 1;

  // Determine visual state of a day
  const getDayState = (dateStr: string): 'check-in' | 'check-out' | 'in-range' | 'preview' | 'normal' => {
    const { checkIn, checkOut } = interval;
    if (checkIn && dateStr === checkIn) return 'check-in';
    if (checkOut && dateStr === checkOut) return 'check-out';
    if (checkIn && checkOut && isDateBetween(dateStr, checkIn, checkOut)) return 'in-range';

    if (
      selectionMode === 'check-out' && checkIn && hoveredDate &&
      compareDates(hoveredDate, checkIn) > 0 &&
      (!minStayFloorDate || compareDates(hoveredDate, minStayFloorDate) >= 0) &&
      getBlockedDatesInRange(checkIn, hoveredDate).length === 0
    ) {
      if (dateStr === hoveredDate || isDateBetween(dateStr, checkIn, hoveredDate)) return 'preview';
    }

    return 'normal';
  };

  // Day is inside the min-stay floor (too short to be a check-out) → dim it.
  const isBelowMinStay = (dateStr: string): boolean =>
    minStayFloorDate !== null &&
    interval.checkIn !== null &&
    compareDates(dateStr, interval.checkIn) > 0 &&
    compareDates(dateStr, minStayFloorDate) < 0;

  const isBestRate = (day: CalendarDay): boolean =>
    hasPriceVariation && day.available && !day.isPast && day.price !== null && day.price === bestRatePrice;

  return (
    <div
      className="pl-4 w-[290px] self-end"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Header — always interactive */}
      <div className="flex justify-between items-center mb-4 border-b border-white/30">
        <button
          className={`bg-transparent border-none text-white text-[25px] cursor-pointer p-2 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-300 hover:bg-white/20 active:scale-95 ${
            !canGoPrev() ? 'opacity-0 pointer-events-none' : ''
          }`}
          onClick={canGoPrev() ? goPrev : undefined}
        >
          <IoChevronBack />
        </button>
        <span className="text-white text-lg font-semibold whitespace-nowrap backdrop-blur-[1px]" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
          {t(`calendar.months.${MONTH_KEYS[month - 1]}`)} {year}
        </span>
        <button
          className="bg-transparent border-none text-white text-[25px] cursor-pointer p-2 rounded-full w-12 h-12 flex items-center justify-center transition-all duration-300 hover:bg-white/20 active:scale-95"
          onClick={goNext}
        >
          <IoChevronForward />
        </button>
      </div>

      {/* Festive min-stay hint (D31) */}
      <MinStayNotice nights={activeMinStay} tone="dark" />

      {/* Calendar grid area — fixed height for 6 rows (max) to prevent layout shift */}
      <div className="h-[165px]">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        ) : (
        <div className="grid grid-cols-7 gap-x-0 gap-y-1">
          {/* Empty cells before first day */}
          {Array.from({ length: startingDayOfWeek }, (_, i) => (
            <div key={`empty-${i}`} className="min-h-[24px]" />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const dayNum = i + 1;
            const dayData = getDayData(dayNum);
            if (!dayData) return <div key={dayNum} className="min-h-[24px]" />;

            const dateStr = dayData.date;
            const isClickable = !dayData.isPast;
            const bestRate = isBestRate(dayData);
            // Festive days (mirrors best-rate gating: only on bookable, non-past days).
            const festive = dayData.isFestive && dayData.available && !dayData.isPast;
            const dayState = getDayState(dateStr);
            const belowMinStay = dayState === 'normal' && isBelowMinStay(dateStr);

            return (
              <div
                key={dayNum}
                className={[
                  'text-white text-base text-center min-h-[24px] flex items-center justify-center relative transition-all duration-200 backdrop-blur-[1px]',
                  dayData.isPast && 'opacity-20 cursor-not-allowed',
                  !dayData.available && !dayData.isPast && dayState === 'normal' && 'opacity-40',
                  isClickable && 'cursor-pointer hover:bg-white/10',
                  !isClickable && 'cursor-not-allowed',
                  bestRate && !festive && dayState === 'normal' && 'text-[#86c98a] font-semibold hover:bg-[#86c98a]/10',
                  festive && dayState === 'normal' && 'text-[#c79a3a] font-semibold hover:bg-[#c79a3a]/10',
                  dayState === 'check-in' && 'bg-white/30 ring-1 ring-white font-bold rounded-l-md z-10',
                  dayState === 'check-out' && 'bg-white/30 ring-1 ring-white font-bold rounded-r-md z-10',
                  dayState === 'in-range' && 'bg-white/15',
                  dayState === 'preview' && 'bg-white/10',
                  dayData.isToday && dayState === 'normal' && 'ring-1 ring-amber-500/50',
                  dayState !== 'check-in' && dayState !== 'check-out' && dayState !== 'in-range' && dayState !== 'preview' && 'rounded-sm',
                  belowMinStay && 'opacity-30',
                  flashDates.has(dateStr) && 'animate-flash-blocked ring-2 ring-red-500 bg-red-500/30 opacity-100',
                ].filter(Boolean).join(' ')}
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                onClick={isClickable ? () => handleDayClick(dayData) : undefined}
                role={isClickable ? 'button' : undefined}
                onMouseEnter={selectionMode === 'check-out' && interval.checkIn ? () => setHoveredDate(dateStr) : undefined}
                onMouseLeave={selectionMode === 'check-out' ? () => setHoveredDate(null) : undefined}
              >
                {dayNum}
                {/* Markers stacked top-right (rate sign on top, min-stay "!"
                    below); "!" suppressed on festive days — the star covers them. */}
                {dayState === 'normal' && (
                  <span className="absolute top-0 right-0.5 z-10 flex flex-col items-end leading-none">
                    {bestRate && !festive && (
                      <span className="text-[10px] leading-none text-[#86c98a]/90">%</span>
                    )}
                    {festive && (
                      <span className="text-[10px] leading-none text-[#c79a3a]/90">✦</span>
                    )}
                    {dayData.minStay > 1 && !festive && (
                      <span className="text-[10px] font-bold leading-none text-red-400" title={`Minimum ${dayData.minStay} nights`}>!</span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
